import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Images, ImagePlus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import DashboardCard from '@/components/ui/DashboardCard';
import GlassPill from '@/components/ui/GlassPill';
import DashboardButton from '@/components/ui/DashboardButton';
import { usePrivateImageLibrary } from '@/hooks/content-generator/private-image-library/use-private-image-library';
import { useDeletePrivateImage, useDownloadPrivateImage } from '@/hooks/content-generator/private-image-library';
import { useDebouncedValue } from '@/lib/utils';
import PrivateImageGrid from './PrivateImageGrid';
import PrivateImageUploadModal from './PrivateImageUploadModal';
import { PrivateImageMetadataSidebar } from './PrivateImageMetadataSidebar';
import ImageDetail from '@/components/image-lightbox/ImageDetail/ImageDetail';
import PrivateImageLibrarySkeleton from './PrivateImageLibrarySkeleton';
import PrivateImageLibraryEmpty from './PrivateImageLibraryEmpty';
import type { PrivateImageLibraryProps, PrivateImageLibraryItem } from '@/hooks/content-generator/private-image-library/types';

const PrivateImageLibrary: React.FC<PrivateImageLibraryProps> = ({
  workbookId, 
  isUploadModalOpen: externalIsUploadModalOpen, 
  onCloseUploadModal,
  onCreateImage,
  autoOpenImageId, // NEW: Destructure auto-open props
  onAutoOpenComplete
}) => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState<PrivateImageLibraryItem | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [internalIsUploadModalOpen, setInternalIsUploadModalOpen] = useState(false);
  
  // Determine if we're on a project page (hide storage buttons)
  const isProjectPage = location.pathname.includes('/project/');
  
  // Use external modal state if provided, otherwise use internal state
  const isUploadModalOpen = externalIsUploadModalOpen !== undefined ? externalIsUploadModalOpen : internalIsUploadModalOpen;
  const closeUploadModal = onCloseUploadModal || (() => setInternalIsUploadModalOpen(false));
  
  // Debounce search to prevent excessive API calls
  const debouncedSearch = useDebouncedValue(searchQuery, 500);
  
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = usePrivateImageLibrary(workbookId, {
    searchQuery: debouncedSearch,
    limit: 12
  });
  
  // Delete and download hooks
  const { mutateAsync: deleteImage } = useDeletePrivateImage();
  const downloadImage = useDownloadPrivateImage();
  
  // Flatten all pages into single array
  const allImages = data?.pages.flatMap(page => page.data) || [];
  const totalCount = data?.pages[0]?.total || 0;
  
  // ✅ DIRECT SYNC: Update selectedImage immediately when project assignments change (like cards)
  const handleProjectUpdate = useCallback((imageId: string, newProjectId: string | null) => {
    if (selectedImage && selectedImage.image_id === imageId) {
      console.log('[ProjectImageLibrary] Direct sync: updating selectedImage project assignment');
      setSelectedImage({
        ...selectedImage,
        workbook_id: newProjectId // ✅ IMMEDIATE: Update project assignment without waiting for cache
      });
    }
  }, [selectedImage]);
  
  // Auto-open image lightbox when requested
  useEffect(() => {
    if (autoOpenImageId) {
      // Check if the image exists in our current data
      const targetImage = allImages.find(item => item.image_id === autoOpenImageId);
      if (targetImage) {
        console.log('[PrivateImageLibrary] Auto-opening lightbox for image:', autoOpenImageId);
        setSelectedImage(targetImage);
        setIsLightboxOpen(true);
        if (onAutoOpenComplete) {
          onAutoOpenComplete();
        }
      }
    }
  }, [autoOpenImageId, allImages, onAutoOpenComplete]);
  
  // Load more function for infinite scroll
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };
  
  // Handle image click - open lightbox
  const handleImageClick = (index: number) => {
    const image = allImages[index];
    if (image) {
      setSelectedImage(image);
      setIsLightboxOpen(true);
    }
  };
  
  // Handle lightbox navigation
  const handleLightboxNavigate = (image: PrivateImageLibraryItem) => {
    setSelectedImage(image);
  };
  
  // Handle lightbox close
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  };
  
  // Custom delete handler for ImageDetail
  const handleCustomDelete = async (image: PrivateImageLibraryItem): Promise<void> => {
    await deleteImage({ imageId: image.image_id, workbookId });
  };
  
  // Custom download handler for ImageDetail
  const handleCustomDownload = async (image: PrivateImageLibraryItem): Promise<void> => {
    await downloadImage(image);
  };
  
  // Property accessors for ImageDetail
  const getImageUrl = (item: PrivateImageLibraryItem): string => item.image_url;
  const getImageId = (item: PrivateImageLibraryItem): string => item.image_id;
  const getImageDescription = (item: PrivateImageLibraryItem): string | undefined => 
    item.generation_prompt || item.alt_text || undefined;
  const getUserId = (item: PrivateImageLibraryItem): string | undefined => undefined; // Private images don't have user_id exposure
  const getFavoriteCount = (item: PrivateImageLibraryItem): number | undefined => undefined; // No favorites for private images
  
  // Custom ownership check - check actual image ownership using user_id
  const checkOwnership = (item: PrivateImageLibraryItem): boolean => {
    // For project images, we need to query the database to check ownership
    // Since PrivateImageLibraryItem doesn't include user_id, we'll rely on 
    // the RLS policies and the fact that users can only see/access their own images
    // However, for better UX, we should ideally check ownership properly
    // For now, return true as RLS will handle the actual permission enforcement
    // TODO: Include user_id in PrivateImageLibraryResponse to enable proper ownership checks
    return true; // RLS policies will enforce actual ownership
  };
  
  // Custom sidebar renderer that excludes filter information
  const renderCustomSidebar = (image: PrivateImageLibraryItem, context: any) => {
    return (
      <PrivateImageMetadataSidebar
        image={image}
        // Don't hardcode isOwner - let the sidebar determine ownership through the same mechanism
        // that ProjectImageAddToProjectButton uses (querying workbook_images.user_id)
        isDeleteLoading={context.isDeleteLoading}
        onDownload={context.onDownload}
        onDelete={context.onDelete}
        hasPrevious={context.hasPrevious}
        hasNext={context.hasNext}
        isLoadingMore={context.isLoadingMore}
        hasNextPage={context.hasNextPage}
        onPrevious={context.onPrevious}
        onNext={context.onNext}
        onLoadMore={context.onLoadMore}
        // ✅ DIRECT SYNC: Pass callback for immediate data updates (Project Library only)
        onProjectUpdate={handleProjectUpdate}
        hideStorageButton={isProjectPage} // NEW: Hide storage button on project pages
      />
    );
  };
  
  return (
    <DashboardCard 
      role="region" 
      aria-labelledby="private-image-library-title"
      data-section="private-image-library"
    >
      <div className="space-y-6">
        {/* Section Header with Image Count and Create Image Button */}
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <Images className="text-blue-500 dark:text-blue-400" size={18} />
            <h3 className="font-medium text-base">Shared Images</h3>
            {totalCount > 0 && (
              <GlassPill 
                type={totalCount.toString()} 
                variant="blue" 
                className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
              />
            )}
          </div>
          
          {/* Create Image Button - Desktop only */}
          {onCreateImage && (
            <div className="hidden sm:block">
              <DashboardButton
                onClick={onCreateImage}
                icon={ImagePlus}
                size="sm"
                className="h-8 px-3 py-1.5 text-sm"
              >
                Create Image
              </DashboardButton>
            </div>
          )}
        </div>
        
        {/* Mobile Create Image Button - Full width below header */}
        {onCreateImage && (
          <div className="sm:hidden mb-4">
            <DashboardButton
              onClick={onCreateImage}
              icon={ImagePlus}
              size="sm"
              className="h-8 px-3 py-1.5 text-sm w-full"
            >
              Create Image
            </DashboardButton>
          </div>
        )}
        
        {/* Search feedback */}
        {debouncedSearch && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Showing results for "{debouncedSearch}"
          </p>
        )}
      </div>
      
      {/* Loading State */}
      {isLoading && <PrivateImageLibrarySkeleton />}
      
      {/* Empty State */}
      {!isLoading && allImages.length === 0 && (
        <PrivateImageLibraryEmpty 
          hasSearch={!!debouncedSearch}
          searchQuery={debouncedSearch}
          onClearSearch={() => setSearchQuery('')}
          onCreateImage={onCreateImage || (() => setInternalIsUploadModalOpen(true))}
        />
      )}
      
      {/* Image Grid */}
      {allImages.length > 0 && (
        <PrivateImageGrid
          images={allImages}
          onImageClick={handleImageClick}
          onLoadMore={loadMore}
          hasMore={hasNextPage || false}
          isLoading={isFetchingNextPage}
          workbookId={workbookId} // NEW: Pass workbookId for project assignment state
          hideStorageButton={isProjectPage}
        />
      )}
      
      {/* Sophisticated Lightbox using existing ImageDetail with custom sidebar */}
      <ImageDetail<PrivateImageLibraryItem>
        image={selectedImage}
        isOpen={isLightboxOpen}
        onClose={handleLightboxClose}
        allImages={allImages}
        onNavigate={handleLightboxNavigate}
        loadMore={loadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        
        // Property accessors for PrivateImageLibraryItem
        getImageUrl={getImageUrl}
        getImageId={getImageId}
        getImageDescription={getImageDescription}
        getUserId={getUserId}
        getFavoriteCount={getFavoriteCount}
        
        // Custom handlers
        onDelete={handleCustomDelete}
        onDownload={handleCustomDownload}
        checkOwnership={checkOwnership}
        
        // Custom sidebar that excludes filter information
        renderSidebar={renderCustomSidebar}
        
        // Display options - disable favorites for private images
        showFavoriteButton={false}
        showDescription={true}
        dropdownLabel="Private Image"
      />
      
      {/* AI Image Magic Modal for Private Library */}
      <PrivateImageUploadModal
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        workbookId={workbookId}
      />
    </DashboardCard>
  );
};

export default PrivateImageLibrary; 